import { bestRecordKey, StorageService } from "../storage-service.ts";
import type { Difficulty, RegionScope } from "../types.ts";
import { difficultyHint, difficultyLabel, mmss, regionLabel, stars, t } from "./i18n.ts";

export interface ManifestEntry {
  file: string;
  regionScope: RegionScope;
  difficulty: Difficulty;
  countryCount: number;
  pieceCount: number;
  bytes: number;
}

const ORDER: Difficulty[] = ["Easy", "Medium", "Hard", "VeryHard"];
const GRID_COLS = 14;
const GRID_ROWS = 9;

export class MenuView {
  private readonly root: HTMLElement;
  private readonly entries: ManifestEntry[];
  private readonly storage: StorageService | null;
  private readonly onStart: (region: RegionScope, difficulty: Difficulty) => void;
  private gridTimer = 0;
  private region: RegionScope = "Europe";
  private difficulty: Difficulty = "Easy";

  constructor(
    root: HTMLElement,
    entries: ManifestEntry[],
    storage: StorageService | null,
    onStart: (region: RegionScope, difficulty: Difficulty) => void,
  ) {
    this.root = root;
    this.entries = entries;
    this.storage = storage;
    this.onStart = onStart;

    this.buildChips();
    this.q<HTMLButtonElement>("menu-start").addEventListener("click", () => this.start());
    this.q<HTMLButtonElement>("menu-records").addEventListener("click", () => this.toggleRecords());
    this.renderInfo();
  }

  private q<T extends HTMLElement>(id: string): T {
    return this.root.querySelector<T>(`#${id}`)!;
  }

  private regions(): RegionScope[] {
    return [...new Set(this.entries.map((e) => e.regionScope))];
  }

  private buildChips(): void {
    const regionBox = this.q("menu-regions");
    for (const region of this.regions()) {
      regionBox.appendChild(this.chip(regionLabel(region), "", () => {
        this.region = region;
        this.refreshSelection();
      }, region));
    }

    const diffBox = this.q("menu-difficulties");
    for (const difficulty of ORDER) {
      diffBox.appendChild(this.chip(difficultyLabel(difficulty), difficultyHint(difficulty), () => {
        this.difficulty = difficulty;
        this.refreshSelection();
      }, difficulty));
    }
    this.refreshSelection();
  }

  private chip(label: string, hint: string, onClick: () => void, value: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "chip";
    button.dataset.value = value;

    const main = document.createElement("span");
    main.textContent = label;
    button.appendChild(main);

    if (hint) {
      const sub = document.createElement("small");
      sub.textContent = hint;
      button.appendChild(sub);
    }
    button.addEventListener("click", onClick);
    return button;
  }

  private refreshSelection(): void {
    for (const chip of this.root.querySelectorAll<HTMLElement>("#menu-regions .chip")) {
      chip.classList.toggle("selected", chip.dataset.value === this.region);
    }
    for (const chip of this.root.querySelectorAll<HTMLElement>("#menu-difficulties .chip")) {
      chip.classList.toggle("selected", chip.dataset.value === this.difficulty);
    }
    this.renderInfo();
  }

  private currentEntry(): ManifestEntry | undefined {
    return this.entries.find((e) => e.regionScope === this.region && e.difficulty === this.difficulty);
  }

  private setStartLabel(text: string): void {
    // 按鈕內含副標與箭頭，只能改 label 節點，不能整顆 textContent 蓋掉
    this.q("menu-start").querySelector<HTMLElement>(".cta-label")!.textContent = text;
  }

  private renderInfo(): void {
    const entry = this.currentEntry();
    const start = this.q<HTMLButtonElement>("menu-start");
    const set = (id: string, value: string) => { this.q(id).textContent = value; };

    if (!entry) {
      for (const id of ["stat-countries", "stat-pieces", "stat-best"]) set(id, "—");
      set("menu-start-sub", t("noData"));
      start.disabled = true;
      return;
    }
    start.disabled = false;

    const record = this.bestFor(this.region, this.difficulty);
    set("stat-countries", String(entry.countryCount));
    set("stat-pieces", String(entry.pieceCount));
    set("stat-best", record ? String(record.bestScore) : "—");
    set("menu-start-sub", record
      ? t("bestLine", { score: record.bestScore, time: mmss(record.bestTimeSeconds), stars: stars(record.stars) })
      : `${regionLabel(this.region)} · ${difficultyLabel(this.difficulty)}`);
  }

  // 背景拼圖格：隨機點亮少數格子，只在選單可見時跑，進遊戲後必須停掉
  private buildGrid(): void {
    const grid = this.q("menu-grid");
    if (grid.childElementCount) return;
    grid.style.gridTemplateColumns = `repeat(${GRID_COLS}, 1fr)`;
    grid.style.gridTemplateRows = `repeat(${GRID_ROWS}, 1fr)`;
    for (let i = 0; i < GRID_COLS * GRID_ROWS; i++) grid.appendChild(document.createElement("i"));
  }

  private startGrid(): void {
    this.buildGrid();
    const cells = [...this.q("menu-grid").children] as HTMLElement[];
    const pick = () => cells[Math.floor(Math.random() * cells.length)];
    const tick = () => {
      for (const cell of cells) cell.className = "";
      const count = Math.floor(Math.random() * 4) + 2;
      for (let i = 0; i < count; i++) pick().className = "lit";
      pick().className = "hot";
    };
    tick();
    window.clearInterval(this.gridTimer);
    this.gridTimer = window.setInterval(tick, 800);
  }

  private stopGrid(): void {
    window.clearInterval(this.gridTimer);
    this.gridTimer = 0;
  }

  private bestFor(region: RegionScope, difficulty: Difficulty) {
    if (!this.storage) return null;
    try {
      return this.storage.loadBestRecord(bestRecordKey(region, difficulty));
    } catch {
      return null; // 紀錄損毀不該擋住開局，結算時才處理重置
    }
  }

  private toggleRecords(): void {
    const panel = this.q("menu-records-panel");
    if (panel.classList.toggle("visible") === false) return;

    let records: ReturnType<StorageService["loadAll"]> = {};
    try {
      records = this.storage?.loadAll() ?? {};
    } catch {
      panel.replaceChildren(Object.assign(document.createElement("p"), { textContent: t("recordsCorrupt") }));
      return;
    }

    const rows = Object.values(records).sort((a, b) => b.bestScore - a.bestScore);
    if (rows.length === 0) {
      panel.replaceChildren(Object.assign(document.createElement("p"), { textContent: t("noRecordsYet") }));
      return;
    }

    const table = document.createElement("table");
    for (const r of rows) {
      const [region, difficulty] = r.key.split("_") as [RegionScope, Difficulty];
      const tr = document.createElement("tr");
      for (const text of [
        `${regionLabel(region)} ${difficultyLabel(difficulty)}`,
        String(r.bestScore),
        mmss(r.bestTimeSeconds),
        stars(r.stars),
      ]) {
        const td = document.createElement("td");
        td.textContent = text;
        tr.appendChild(td);
      }
      table.appendChild(tr);
    }
    panel.replaceChildren(table);
  }

  private start(): void {
    const start = this.q<HTMLButtonElement>("menu-start");
    start.disabled = true;
    this.setStartLabel(t("loading"));
    this.q("menu-error").textContent = "";
    this.onStart(this.region, this.difficulty);
  }

  /** 載入失敗時停留在選單並可重試（SRS AC-1.2） */
  showError(message: string): void {
    this.q("menu-error").textContent = message;
    const start = this.q<HTMLButtonElement>("menu-start");
    start.disabled = false;
    this.setStartLabel(t("retry"));
  }

  /** 語言切換後呼叫：晶片文字與資訊列都要重生成 */
  refresh(): void {
    this.q("menu-regions").replaceChildren();
    this.q("menu-difficulties").replaceChildren();
    this.buildChips();
    if (!this.q<HTMLButtonElement>("menu-start").disabled) this.setStartLabel(t("start"));
    if (this.q("menu-records-panel").classList.contains("visible")) {
      this.q("menu-records-panel").classList.remove("visible");
      this.toggleRecords();
    }
  }

  show(): void {
    this.root.hidden = false;
    this.startGrid();
    this.q<HTMLButtonElement>("menu-start").disabled = false;
    this.setStartLabel(t("start"));
    this.q("menu-error").textContent = "";
    this.q("menu-records-panel").classList.remove("visible");
    this.renderInfo();
  }

  hide(): void {
    this.stopGrid();
    this.root.hidden = true;
  }

  select(region: RegionScope, difficulty: Difficulty): void {
    this.region = region;
    this.difficulty = difficulty;
    this.refreshSelection();
  }
}
